/**
 * Shared quotation-number parsing and formatting.
 *
 * New quotation numbers use `BASE RE N` for revisions. The legacy
 * `/Rev-N` form is still accepted so existing records remain navigable.
 */
export interface ParsedQuotationNumber {
  quotationNo: string;
  baseQuotationNo: string;
  revision: number;
  revisionLabel: string;
  isLegacyRevision: boolean;
}

const NEW_REVISION_SUFFIX = /\s+RE\s+(\d+)$/i;
const LEGACY_REVISION_SUFFIX = /\/Rev-(\d+)$/i;

const toRevisionNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }

  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(?:RE\s*|Rev-\s*)?(\d+)$/i);
  return match ? Math.max(0, parseInt(match[1], 10)) : null;
};

export const formatRevisionLabel = (revision: number): string => {
  const normalizedRevision = Math.max(0, Math.trunc(revision));
  return normalizedRevision > 0 ? `RE ${normalizedRevision}` : '';
};

export const parseQuotationNumber = (
  value: string | null | undefined,
): ParsedQuotationNumber => {
  const quotationNo = typeof value === 'string' ? value.trim() : '';
  const newMatch = quotationNo.match(NEW_REVISION_SUFFIX);
  const legacyMatch = quotationNo.match(LEGACY_REVISION_SUFFIX);
  const revision = newMatch
    ? parseInt(newMatch[1], 10)
    : legacyMatch
      ? parseInt(legacyMatch[1], 10)
      : 0;
  const suffixMatch = newMatch || legacyMatch;
  const baseQuotationNo = suffixMatch
    ? quotationNo.slice(0, suffixMatch.index).trim()
    : quotationNo;

  return {
    quotationNo,
    baseQuotationNo,
    revision,
    revisionLabel: formatRevisionLabel(revision),
    isLegacyRevision: Boolean(legacyMatch),
  };
};

export const getBaseQuotationNo = (quotationNo: string | null | undefined): string =>
  parseQuotationNumber(quotationNo).baseQuotationNo;

export const getQuotationRevision = (
  quotationNoOrRevision: string | number | null | undefined,
  fallbackQuotationNo?: string | null,
): number => {
  const directRevision = toRevisionNumber(quotationNoOrRevision);
  if (directRevision !== null) return directRevision;
  if (typeof quotationNoOrRevision === 'string' && quotationNoOrRevision.trim()) {
    const parsed = parseQuotationNumber(quotationNoOrRevision);
    if (parsed.revision > 0 || parsed.isLegacyRevision) return parsed.revision;
  }
  return parseQuotationNumber(fallbackQuotationNo).revision;
};

export const getQuotationRevisionLabel = (
  quotationNoOrRevision: string | number | null | undefined,
  fallbackQuotationNo?: string | null,
): string => formatRevisionLabel(getQuotationRevision(quotationNoOrRevision, fallbackQuotationNo));

/** Format a base quotation and revision using the current backend format. */
export const formatQuotationNumber = (quotationNo: string, revision: number): string => {
  const baseQuotationNo = getBaseQuotationNo(quotationNo);
  const normalizedRevision = Math.max(0, Math.trunc(revision));
  return normalizedRevision > 0
    ? `${baseQuotationNo} ${formatRevisionLabel(normalizedRevision)}`
    : baseQuotationNo;
};

export const quotationRevisionComparator = (
  first: string | null | undefined,
  second: string | null | undefined,
): number => parseQuotationNumber(first).revision - parseQuotationNumber(second).revision;
